import { types as sdkTypes } from './sdkLoader';

// Safe env accessor (Webpack 5 / Vite may not polyfill process automatically in browser)
// Avoid referencing undeclared global 'process' directly which triggers ReferenceError.
const ENV = (typeof process !== 'undefined' && process && process.env) ? process.env : {};

// =============================================================
// Line Item Classification & Builder (Stripe Connect readiness)
// =============================================================
// We currently have a single provider in Flex. To prepare for future
// multi-party payout routing (e.g., external liquor store, Crown Produce),
// we classify each line item with a code that maps to an intended payee group.
// Server-side (or reporting) logic can later map these codes to Stripe
// destination accounts or revenue splits. For now, all items are still
// charged to the single provider/marketplace; codes are structured for
// forward compatibility.
//
// Payee groups (conceptual routing buckets for future Stripe Connect payouts):
// - provider: service provider (chef, bartender, food truck) – immediate payout eligible
// - liquor: external liquor supplier bucket (deferred payout & later transfer) – deferred
// - crown: Crown Produce (juice, ice, garnish & similar) – deferred
// - glassware: (future) external glassware/rental vendor – deferred
// - marketplace: platform fees & (provisional) tax handling – stays on platform
//
// Canonical line-item codes (prefix 'line-item/') & groups:
// Provider-earned:
//   food, cocktail, service-fee, cocktail-service-fee, misc,
//   custom-ingredient-non-alcohol
// Vendor channels (deferred transfer later):
//   alcohol (liquor), juice (crown), ice (crown), garnish (crown), glassware (glassware)
// Platform:
//   booking-fee, tax
//
// NOTE: Alcohol custom ingredients now map to 'line-item/alcohol' (liquor group) since
// providers cannot directly sell alcohol. Only non-alcohol custom ingredients retain a
// custom-specific code.
// Exported for server-side parity (ensure any server copy stays in sync)
export const PAYEE_CODE_MAP = Object.freeze({
  // Provider
  'line-item/food': 'provider',
  'line-item/cocktail': 'provider',
  'line-item/service-fee': 'provider',
  'line-item/cocktail-service-fee': 'provider',
  'line-item/misc': 'provider',
  'line-item/custom-ingredient-non-alcohol': 'provider',
  // Vendor (Liquor)
  'line-item/alcohol': 'liquor',
  // Vendor (Crown Produce)
  'line-item/juice': 'crown',
  'line-item/ice': 'crown',
  'line-item/garnish': 'crown',
  // Vendor (Glassware/Rental)
  'line-item/glassware': 'glassware',
  // Platform
  'line-item/booking-fee': 'marketplace',
  'line-item/tax': 'marketplace',
  // Provider commission (negative provider line; maps to provider so provider net auto-computes)
  'line-item/provider-booking-fee': 'provider',
});

// Pricing schema/versioning for forward compatibility
export const PRICING_SCHEMA_VERSION = 1;

// Resolve pricing configuration (commission percentages, platform booking fee, etc.)
// Priority: explicit arg override > listing publicData/custom fields > environment vars > default.
export function resolvePricingConfig(listing = {}, overrides = {}) {
  // Pull from either REACT_APP_* (CRA style) or plain variable names; fallback to defaults.
  const envProviderPct = parseFloat(ENV.REACT_APP_PROVIDER_BOOKING_FEE_PERCENT || ENV.PROVIDER_BOOKING_FEE_PERCENT || '0.10');
  const envCustomerBookingFeeFixed = parseFloat(ENV.REACT_APP_CUSTOMER_BOOKING_FEE || ENV.CUSTOMER_BOOKING_FEE || '0'); // absolute amount
  const envCustomerBookingFeePct = parseFloat(ENV.REACT_APP_CUSTOMER_BOOKING_FEE_PERCENT || ENV.CUSTOMER_BOOKING_FEE_PERCENT || '0.10');
  const fromListing = listing?.publicData?.pricingConfig || {};
  const providerPct = overrides.providerBookingFeePercent ?? fromListing.providerBookingFeePercent ?? envProviderPct ?? 0.10;
  const bookingFee = overrides.bookingFee ?? fromListing.bookingFee ?? envCustomerBookingFeeFixed ?? 0; // explicit amount wins
  const customerBookingFeePercent = overrides.customerBookingFeePercent ?? fromListing.customerBookingFeePercent ?? envCustomerBookingFeePct ?? 0.10;
  return {
    providerBookingFeePercent: providerPct,
    bookingFee,
    customerBookingFeePercent,
  };
}

// Heuristic classifier for add-ons & custom items.
/**
 * Heuristic classifier mapping arbitrary add-on item objects to a canonical line-item code.
 * @param {Object} item
 * @returns {string} canonical line-item/ code
 */
function classifyItem(item) {
  if (!item) return 'line-item/misc';
  const id = item.id || item.catalogId || '';
  const name = (item.name || '').toLowerCase();
  const category = (item.category || '').toLowerCase();

  // Custom cocktail builder ingredients: alcohol variant maps to liquor group via 'line-item/alcohol'
  if (item.isCustom === true || item.source === 'custom') {
    if ((item.type || '').toLowerCase() === 'alcohol') {
      return 'line-item/alcohol';
    }
    return 'line-item/custom-ingredient-non-alcohol';
  }

  // Extended food keyword detection (names often lack explicit category)
  const FOOD_NAME_KEYWORDS = [
    'food','meal','dish','menu','entree','entrée','appetizer','starter','snack','side','dessert','catering','plate','platter','taco','tacos','pizza','burger','sandwich','salad','wrap','bowl','rice','beans','noodle','pasta','sushi','roll','steak','chicken','beef','pork','shrimp','salmon','fish','carne','asada','quesadilla','enchilada','fajita','carnitas'];
  if (FOOD_NAME_KEYWORDS.some(k => name.includes(k) || category.includes(k))) return 'line-item/food';
  // Food: explicitly flagged or categorized
  if (item.isFood || category === 'food') return 'line-item/food';
  // Juice
  if (category === 'juice' || id.startsWith('juice_')) return 'line-item/juice';
  // Ice (treat as Crown Produce)
  if (category === 'ice' || name.includes('ice')) return 'line-item/ice';
  // Alcohol categories: liquor, beer, wine, champagne, seltzer
  if (['liquor','beer','wine','champagne','seltzer','alcohol'].includes(category)) return 'line-item/alcohol';
  // Garnish detection
  if (item.isGarnish || category === 'garnish' || name.includes('garnish')) return 'line-item/garnish';
  // Fallback to misc (provider bucket)
  return 'line-item/misc';
}

function money(amount, currency='USD') { return new sdkTypes.Money(Math.round(amount * 100), currency); }

// New detailed builder (non-breaking: legacy buildLineItems retained below)
export function buildLineItemsDetailed(listing, {
  cocktails = [],
  addOns = [],
  foodItems = [],
  baseServiceCharge = 0,
  totalServiceFees = 0,
  iceCost = 0,
  glasswareCost = 0, // currently treated as provider cost (service provider supplies)
  bookingFee, // may be resolved via config resolver if undefined
  taxAmount = 0,
  currency = 'USD',
  includeTax = true,
  providerBookingFeePercent = 0.10, // 10% fee charged to provider (negative line item)
  customerBookingFeePercent, // optional percent if bookingFee not explicitly passed
} = {}) {
  const lineItems = [];

  // Resolve config (supports env & listing overrides)
  const resolved = resolvePricingConfig(listing, { providerBookingFeePercent, bookingFee, customerBookingFeePercent });
  providerBookingFeePercent = resolved.providerBookingFeePercent;
  if (bookingFee == null) bookingFee = resolved.bookingFee;
  if (customerBookingFeePercent == null) customerBookingFeePercent = resolved.customerBookingFeePercent;
  // Clamp provider commission to safety max (25%)
  const MAX_PROVIDER_PCT = 0.25;
  if (providerBookingFeePercent > MAX_PROVIDER_PCT) providerBookingFeePercent = MAX_PROVIDER_PCT;
  if (providerBookingFeePercent < 0) providerBookingFeePercent = 0;

  // Accept both positive and negative (for commission) but skip zero-value lines
  const push = li => { if (li.unitPrice.amount !== 0) lineItems.push(li); };

  // Service fees (provider)
  if (baseServiceCharge > 0) push({ code: 'line-item/service-fee', unitPrice: money(baseServiceCharge, currency), quantity: 1, includeFor: ['customer','provider'] });
  if (totalServiceFees > 0) push({ code: 'line-item/cocktail-service-fee', unitPrice: money(totalServiceFees, currency), quantity: 1, includeFor: ['customer','provider'] });

  // Cocktails: treat cost per unit across quantity; classification stays 'cocktail'
  cocktails.forEach(ct => {
    const qty = ct.quantity ?? ct.qty ?? 0;
    if (qty <= 0) return;
    let perUnit = ct.price;
    const totalCost = ct.cost;
    if ((perUnit == null || perUnit <= 0) && totalCost > 0 && qty > 0) perUnit = totalCost / qty;
    if (perUnit > 0) push({ code: 'line-item/cocktail', unitPrice: money(perUnit, currency), quantity: qty, includeFor: ['customer','provider'], name: ct.name });
  });

  // Food items (provider direct)
  foodItems.forEach(fi => {
    const qty = fi.quantity ?? fi.qty ?? 0;
    if (qty <= 0) return;
    let perUnit = fi.price;
    if ((perUnit == null || perUnit <= 0) && fi.cost > 0 && qty > 0) perUnit = fi.cost / qty;
    if (perUnit > 0) push({ code: 'line-item/food', unitPrice: money(perUnit, currency), quantity: qty, includeFor: ['customer','provider'], name: fi.name });
  });

  // Add-ons (alcohol, juice, ice, misc)
  addOns.forEach(item => {
    const qty = item.quantity ?? item.qty ?? 0;
    if (qty <= 0) return;
    let perUnit = item.price;
    if ((perUnit == null || perUnit <= 0) && item.cost > 0 && qty > 0) perUnit = item.cost / qty;
    if (!(perUnit > 0)) return;
    const code = classifyItem(item);
    push({ code, unitPrice: money(perUnit, currency), quantity: qty, includeFor: ['customer','provider'], name: item.name });
  });

  // Ice cost passed separately (Crown) if not already an item
  if (iceCost > 0 && !lineItems.some(li => li.code === 'line-item/ice')) {
    push({ code: 'line-item/ice', unitPrice: money(iceCost, currency), quantity: 1, includeFor: ['customer','provider'] });
  }

  // Glassware (provider)
  if (glasswareCost > 0) push({ code: 'line-item/glassware', unitPrice: money(glasswareCost, currency), quantity: 1, includeFor: ['customer','provider'], name: 'Glassware Rental' });

  // Booking fee (marketplace) – if fixed bookingFee given use it, else if percent provided compute on subtotal (excluding tax & existing marketplace lines)
  if (bookingFee == null || bookingFee === 0) {
    if (customerBookingFeePercent > 0) {
      const baseCents = lineItems
        .filter(li => li.unitPrice.amount > 0 && PAYEE_CODE_MAP[li.code] !== 'marketplace')
        .reduce((sum, li) => sum + (li.unitPrice.amount * li.quantity), 0);
      const feeCents = Math.round(baseCents * customerBookingFeePercent);
      if (feeCents > 0) push({ code: 'line-item/booking-fee', unitPrice: new sdkTypes.Money(feeCents, currency), quantity: 1, includeFor: ['customer','provider'], description: `Customer booking fee ${Math.round(customerBookingFeePercent*100)}%` });
    }
  } else if (bookingFee > 0) {
    push({ code: 'line-item/booking-fee', unitPrice: money(bookingFee, currency), quantity: 1, includeFor: ['customer','provider'] });
  }
  // Tax (marketplace provisional)
  if (includeTax && taxAmount > 0) push({ code: 'line-item/tax', unitPrice: money(taxAmount, currency), quantity: 1, includeFor: ['customer','provider'] });

  // Provider booking fee (commission) – negative line item applied only to provider side.
  if (providerBookingFeePercent > 0) {
    // Sum provider-attributed positive line items BEFORE commission.
    const providerGrossCents = lineItems
      .filter(li => PAYEE_CODE_MAP[li.code] === 'provider')
      .reduce((sum, li) => sum + (li.unitPrice.amount * li.quantity), 0);
    if (providerGrossCents > 0) {
      const feeCents = Math.round(providerGrossCents * providerBookingFeePercent);
      if (feeCents > 0) {
        lineItems.push({
          code: 'line-item/provider-booking-fee',
          unitPrice: new sdkTypes.Money(-feeCents, currency), // negative commission charged to provider
          quantity: 1,
          includeFor: ['provider'],
          description: `Provider booking fee ${Math.round(providerBookingFeePercent * 100)}%`,
        });
      }
    }
  }

  return lineItems;
}

/**
 * Build Sharetribe line items for a booking inquiry/order.
 *
 * Accepts items that may have either:
 * - price (number|string per unit) and quantity, OR
 * - cost (total for item) and qty (quantity), from CheckoutPage computed arrays
 */
export function buildLineItems(
  listing,
  cocktails = [],
  addOns = [],
  baseServiceCharge = 0,
  totalServiceFees = 0,
  iceCost = 0,
  glasswareCost = 0,
  currency = 'USD',
  bookingFee = undefined,        // optional (platform) (resolved if undefined)
  taxAmount = 0,         // optional (platform provisional)
  includeTax = true      // mirror detailed builder toggle
  ,providerBookingFeePercent = 0.10 // 10% default provider fee
  ,customerBookingFeePercent // optional; if bookingFee omitted will compute percent
) {
  const lineItems = [];

  // Resolve config if bookingFee explicit not passed
  const resolved = resolvePricingConfig(listing, { providerBookingFeePercent, bookingFee, customerBookingFeePercent });
  providerBookingFeePercent = resolved.providerBookingFeePercent;
  if (bookingFee == null) bookingFee = resolved.bookingFee;
  if (customerBookingFeePercent == null) customerBookingFeePercent = resolved.customerBookingFeePercent;
  const MAX_PROVIDER_PCT = 0.25;
  if (providerBookingFeePercent > MAX_PROVIDER_PCT) providerBookingFeePercent = MAX_PROVIDER_PCT;
  if (providerBookingFeePercent < 0) providerBookingFeePercent = 0;

  const parseAmount = val => {
    if (val == null) return 0;
    const n = typeof val === 'number' ? val : parseFloat(val);
    return Number.isFinite(n) ? n : 0;
  };
  const getQty = item => {
    const q = item?.quantity ?? item?.qty ?? 0;
    const n = typeof q === 'number' ? q : parseInt(q, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };
  const unitMoney = (amount, curr = currency) => new sdkTypes.Money(Math.round(parseAmount(amount) * 100), curr);

  // Base service fee
  if (parseAmount(baseServiceCharge) > 0) {
    lineItems.push({
      code: 'line-item/service-fee',
      unitPrice: unitMoney(baseServiceCharge),
      quantity: 1,
      includeFor: ['customer', 'provider'],
    });
  }

  // Per-cocktail service fees
  if (parseAmount(totalServiceFees) > 0) {
    lineItems.push({
      code: 'line-item/cocktail-service-fee',
      unitPrice: unitMoney(totalServiceFees),
      quantity: 1,
      includeFor: ['customer', 'provider'],
    });
  }

  // Normalize and push cocktail items (ingredient costs)
  cocktails.forEach(item => {
    const qty = getQty(item);
    if (qty <= 0) return;
    // Prefer per-unit price if provided, else derive from total cost
    let perUnit = parseAmount(item.price);
    if (perUnit <= 0 && parseAmount(item.cost) > 0) {
      perUnit = parseAmount(item.cost) / qty;
    }
    if (perUnit > 0) {
      lineItems.push({
        code: 'line-item/cocktail',
        unitPrice: unitMoney(perUnit),
        quantity: qty,
        includeFor: ['customer', 'provider'],
        name: item.name,
      });
    }
  });

  // Normalize and push add-ons (beer, wine, soda, snacks, food, custom ingredients, etc.)
  addOns.forEach(item => {
    const qty = getQty(item);
    if (qty <= 0) return;
    let perUnit = parseAmount(item.price);
    if (perUnit <= 0 && parseAmount(item.cost) > 0) {
      perUnit = parseAmount(item.cost) / qty;
    }
    if (perUnit > 0) {
      lineItems.push({
        code: classifyItem(item),
        unitPrice: unitMoney(perUnit),
        quantity: qty,
        includeFor: ['customer', 'provider'],
        name: item.name,
      });
    }
  });

  // Ice
  if (parseAmount(iceCost) > 0 && !lineItems.some(li => li.code === 'line-item/ice')) {
    lineItems.push({
      code: 'line-item/ice',
      unitPrice: unitMoney(iceCost),
      quantity: 1,
      includeFor: ['customer', 'provider'],
    });
  }

  // Glassware (provider)
  if (parseAmount(glasswareCost) > 0) {
    lineItems.push({
      code: 'line-item/glassware',
      unitPrice: unitMoney(glasswareCost),
      quantity: 1,
      includeFor: ['customer', 'provider'],
    });
  }

  // Booking fee (platform) – if fixed amount not supplied, compute percent on subtotal
  if (bookingFee == null || parseAmount(bookingFee) === 0) {
    if (customerBookingFeePercent > 0) {
      const baseCents = lineItems
        .filter(li => li.unitPrice.amount > 0 && PAYEE_CODE_MAP[li.code] !== 'marketplace')
        .reduce((sum, li) => sum + (li.unitPrice.amount * li.quantity), 0);
      const feeCents = Math.round(baseCents * customerBookingFeePercent);
      if (feeCents > 0) {
        lineItems.push({
          code: 'line-item/booking-fee',
          unitPrice: new sdkTypes.Money(feeCents, currency),
          quantity: 1,
          includeFor: ['customer', 'provider'],
          description: `Customer booking fee ${Math.round(customerBookingFeePercent*100)}%`,
        });
      }
    }
  } else if (parseAmount(bookingFee) > 0) {
    lineItems.push({
      code: 'line-item/booking-fee',
      unitPrice: unitMoney(bookingFee),
      quantity: 1,
      includeFor: ['customer', 'provider'],
    });
  }

  // Tax (platform provisional)
  if (includeTax && parseAmount(taxAmount) > 0) {
    lineItems.push({
      code: 'line-item/tax',
      unitPrice: unitMoney(taxAmount),
      quantity: 1,
      includeFor: ['customer', 'provider'],
    });
  }

  // Provider booking fee (negative commission) 10% default
  if (providerBookingFeePercent > 0) {
    const providerGrossCents = lineItems
      .filter(li => PAYEE_CODE_MAP[li.code] === 'provider' && li.unitPrice.amount > 0)
      .reduce((sum, li) => sum + (li.unitPrice.amount * li.quantity), 0);
    if (providerGrossCents > 0) {
      const feeCents = Math.round(providerGrossCents * providerBookingFeePercent);
      if (feeCents > 0) {
        lineItems.push({
          code: 'line-item/provider-booking-fee',
          unitPrice: new sdkTypes.Money(-feeCents, currency),
          quantity: 1,
          includeFor: ['provider'],
          description: `Provider booking fee ${Math.round(providerBookingFeePercent * 100)}%`,
        });
      }
    }
  }

  return lineItems;
}

// Utility to expose payee group for analytics/debugging
export function mapLineItemsToPayeeGroups(lineItems) {
  return lineItems.map(li => ({ ...li, payeeGroup: PAYEE_CODE_MAP[li.code] || 'provider' }));
}

// Enrich line items with payout routing metadata for Stripe Connect orchestration.
// This does not execute transfers; it prepares a normalized structure that the
// server (or a future webhook handler) can consume to decide how/when to create
// transfer / payment intents.
//
// accounts shape (all optional): {
//   providerAccountId,
//   liquorStoreAccountId,
//   crownProduceAccountId,
// }
export function enrichLineItemsForPayout(lineItems, { transactionId, accounts = {} } = {}) {
  return lineItems.map(li => {
    const originalGroup = PAYEE_CODE_MAP[li.code] || 'provider';
    // Temporary business rule: liquor & crown funds stay on platform for now (no vendor payouts yet)
    let payoutRecipient = originalGroup;
    if (originalGroup === 'liquor' || originalGroup === 'crown') {
      payoutRecipient = 'marketplace';
    }
    const timing = (payoutRecipient === 'glassware') ? 'deferred' : 'immediate';
    let destination = null;
    if (payoutRecipient === 'provider') destination = accounts.providerAccountId || null;
    else if (payoutRecipient === 'liquor') destination = accounts.liquorStoreAccountId || null; // (unused while rerouted)
    else if (payoutRecipient === 'crown') destination = accounts.crownProduceAccountId || null; // (unused while rerouted)
    else if (payoutRecipient === 'glassware') destination = accounts.glasswareVendorAccountId || null;
    // marketplace capture: destination null
    return {
      ...li,
      payeeGroup: originalGroup, // retain original classification for reporting/order forms
      metadata: {
        originalGroup,
        payoutRecipient,
        timing,
        transferGroup: transactionId ? `txn:${transactionId}` : undefined,
        destination,
  pricingSchemaVersion: PRICING_SCHEMA_VERSION,
      },
    };
  });
}

// Aggregate totals (in minor units) per payee group for payout logic.
// Returns object: { provider: number, liquor: number, crown: number, marketplace: number, currency }
export function sumByPayeeGroup(lineItems) {
  const totals = { provider: 0, liquor: 0, crown: 0, glassware: 0, marketplace: 0 };
  let currency = null;
  lineItems.forEach(li => {
    const group = PAYEE_CODE_MAP[li.code] || 'provider';
    const lineCurrency = li.unitPrice?.currency;
    if (!currency && lineCurrency) currency = lineCurrency;
    const amount = (li.unitPrice?.amount || 0) * (li.quantity || 0);
    totals[group] = (totals[group] || 0) + amount;
  });
  return { ...totals, currency };
}

// Financial summary helper consolidating core monetary figures.
// Returns amounts in minor units (cents) for consistency.
// Shape: {
//   currency,
//   providerGross, providerCommission, providerNet,
//   platformBookingFee, platformTax, platformOther,
//   platformTotalRevenue, // booking fee + commission + tax for now
//   vendorHeld: { liquor, crown, glassware },
// }
export function computeFinancialSummary(lineItems) {
  let currency = null;
  let providerGross = 0;
  let providerCommission = 0; // absolute value of negative provider booking fee lines
  let platformBookingFee = 0;
  let platformTax = 0;
  let platformOther = 0;
  const vendorHeld = { liquor: 0, crown: 0, glassware: 0 };

  lineItems.forEach(li => {
    const group = PAYEE_CODE_MAP[li.code] || 'provider';
    const lineCurrency = li.unitPrice?.currency;
    if (!currency && lineCurrency) currency = lineCurrency;
    const amount = (li.unitPrice?.amount || 0) * (li.quantity || 0);
    if (group === 'provider') {
      if (li.code === 'line-item/provider-booking-fee') {
        // commission stored as negative; accumulate absolute
        providerCommission += Math.abs(amount);
      } else if (amount > 0) {
        providerGross += amount;
      }
    } else if (group === 'marketplace') {
      if (li.code === 'line-item/booking-fee') platformBookingFee += amount;
      else if (li.code === 'line-item/tax') platformTax += amount;
      else platformOther += amount;
    } else if (group === 'liquor' || group === 'crown' || group === 'glassware') {
      if (amount > 0) vendorHeld[group] += amount;
    }
  });

  const providerNet = providerGross - providerCommission;
  const platformTotalRevenue = platformBookingFee + providerCommission + platformTax + platformOther;

  return {
    currency,
    providerGross,
    providerCommission,
    providerNet,
    platformBookingFee,
    platformTax,
    platformOther,
    platformTotalRevenue,
    vendorHeld,
  };
}

// ==============================================
// Refund Allocation Helper
// ==============================================
// Given original lineItems and a desired refund amount (customer perspective),
// allocate refund proportionally across: providerNet (provider positive lines minus commission),
// platform components (booking fee, commission, tax, other) and vendorHeld buckets.
// This enables consistent reversal logic even before multi-vendor payouts exist.
// Returns structure with per-group refund amounts in minor units.
export function allocateRefund(lineItems, refundCustomerTotalCents) {
  if (refundCustomerTotalCents <= 0) return { provider: 0, platform: { bookingFee: 0, commission: 0, tax: 0, other: 0 }, vendors: { liquor: 0, crown: 0, glassware: 0 }, total: 0 };
  const summary = computeFinancialSummary(lineItems);
  const providerNet = summary.providerNet;
  const platformParts = {
    bookingFee: summary.platformBookingFee,
    commission: summary.providerCommission, // commission retained by platform
    tax: summary.platformTax,
    other: summary.platformOther,
  };
  const vendors = { ...summary.vendorHeld };
  const baseTotal = providerNet + platformParts.bookingFee + platformParts.commission + platformParts.tax + platformParts.other + vendors.liquor + vendors.crown + vendors.glassware;
  if (baseTotal === 0) return { provider: 0, platform: { bookingFee: 0, commission: 0, tax: 0, other: 0 }, vendors: { liquor: 0, crown: 0, glassware: 0 }, total: 0 };
  const ratio = Math.min(refundCustomerTotalCents / baseTotal, 1);
  const alloc = (val) => Math.round(val * ratio);
  const result = {
    provider: alloc(providerNet),
    platform: {
      bookingFee: alloc(platformParts.bookingFee),
      commission: alloc(platformParts.commission),
      tax: alloc(platformParts.tax),
      other: alloc(platformParts.other),
    },
    vendors: {
      liquor: alloc(vendors.liquor),
      crown: alloc(vendors.crown),
      glassware: alloc(vendors.glassware),
    },
  };
  const tally = result.provider + result.platform.bookingFee + result.platform.commission + result.platform.tax + result.platform.other + result.vendors.liquor + result.vendors.crown + result.vendors.glassware;
  // Adjust any rounding drift onto platform.other (smallest impact bucket)
  const drift = refundCustomerTotalCents - tally;
  if (Math.abs(drift) > 0) result.platform.other += drift;
  result.total = tally + drift;
  return result;
}
